import { useState } from 'react';
import { StatusBadge } from './StatusBadge';
import type { MergedTimelineEvent } from '../lib/timeline';

// Shared visual timeline, previously three divergent implementations (a rich dot/badge
// timeline on OperationsCenterPage, a bare <ul> on each AS2 message detail page, and flat
// unmerged tables on ExchangeDetailPage). Events are pre-merged/sorted via
// src/lib/timeline.ts's mergeTimelineEvents(). Consecutive runs of 3+ "routine" events (see
// TimelineEvent.routine) collapse into one expandable summary node so a long run of ordinary
// successful steps doesn't bury the errors/retries an operator actually needs to see.
export function Timeline({ events, emptyLabel = 'No timeline events recorded yet.' }: {
  events: MergedTimelineEvent[];
  emptyLabel?: string;
}) {
  if (events.length === 0) {
    return <p>{emptyLabel}</p>;
  }

  return (
    <div className="trace-timeline">
      {groupRoutineRuns(events).map((group) => (
        group.kind === 'single'
          ? <TimelineNode key={group.event.id} event={group.event} />
          : <RoutineRunNode key={group.id} events={group.events} />
      ))}
    </div>
  );
}

function TimelineNode({ event }: { event: MergedTimelineEvent }) {
  return (
    <article className={`trace-node${event.tone ? ` trace-node-${event.tone}` : ''}`}>
      <span className={`trace-dot${event.tone ? ` trace-dot-${event.tone}` : ''}`} />
      <div>
        <header>
          <strong>{event.label}</strong>
          {event.status ? <StatusBadge value={event.status} /> : null}
        </header>
        {event.detail ? <p>{event.detail}</p> : null}
        <span>{new Date(event.at).toLocaleString('en-GB')}</span>
      </div>
    </article>
  );
}

function RoutineRunNode({ events }: { events: MergedTimelineEvent[] }) {
  const [expanded, setExpanded] = useState(false);
  const first = events[0];
  const last = events[events.length - 1];

  return (
    <article className="trace-node trace-node-routine">
      <span className="trace-dot trace-dot-routine" />
      <div>
        <header>
          <strong>{events.length} routine steps completed</strong>
          <button type="button" className="ghost-button" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Collapse' : 'Show steps'}
          </button>
        </header>
        <span>{new Date(first.at).toLocaleString('en-GB')} - {new Date(last.at).toLocaleString('en-GB')}</span>
        {expanded ? (
          <div className="trace-timeline trace-timeline-nested">
            {events.map((event) => <TimelineNode key={event.id} event={event} />)}
          </div>
        ) : null}
      </div>
    </article>
  );
}

type RoutineGroup =
  | { kind: 'single'; event: MergedTimelineEvent }
  | { kind: 'run'; id: string; events: MergedTimelineEvent[] };

// Collapsing threshold: a run of 1-2 routine events isn't worth hiding behind a toggle (it
// doesn't meaningfully reduce clutter), so only runs of 3+ collapse.
const MIN_ROUTINE_RUN_LENGTH = 3;

function groupRoutineRuns(events: MergedTimelineEvent[]): RoutineGroup[] {
  const groups: RoutineGroup[] = [];
  let run: MergedTimelineEvent[] = [];

  function flushRun() {
    if (run.length === 0) return;
    if (run.length >= MIN_ROUTINE_RUN_LENGTH) {
      groups.push({ kind: 'run', id: `run-${run[0].id}`, events: run });
    } else {
      run.forEach((event) => groups.push({ kind: 'single', event }));
    }
    run = [];
  }

  for (const event of events) {
    if (event.routine && event.tone !== 'error') {
      run.push(event);
    } else {
      flushRun();
      groups.push({ kind: 'single', event });
    }
  }
  flushRun();

  return groups;
}
